<template>
    <b-container fluid>
        <admin-page-header title="문서 사실 추출"
                           description="첨부에서 사실(facts)을 미리 추출해 두면 AI 평가·보완검토가 그대로 재사용합니다. 추출이 안 된 첨부는 판정 때 원문이 통째로 들어가 비용이 크게 늘어납니다.">
            <template #actions>
                <span v-if="extractingCount" class="small mr-3"
                      :class="pollingStopped ? 'text-warning' : 'text-muted'"
                >
                    <template v-if="pollingStopped">
                        {{ extractingCount }}건 추출 중 — 자동 갱신이 멈췄습니다. [새로고침]으로 확인하세요
                    </template>
                    <template v-else>
                        <b-spinner small class="mr-1"/>
                        {{ extractingCount }}건 추출 중 — 10초마다 자동 갱신
                    </template>
                </span>
                <!-- 지금 고른 기간·프로젝트·업체를 실어 보내 배치 화면의 '돌아가기'가 그 선택으로 돌아오게 한다 -->
                <b-btn variant="outline-primary" size="sm" class="mr-2"
                       :to="{name: 'DocumentFactBatches', query: selectionQuery}"
                >
                    배치 관리
                </b-btn>
                <b-btn :variant="pollingStopped && extractingCount ? 'primary' : 'outline-secondary'" size="sm"
                       :disabled="!canReload || loadingList"
                       @click="reloadAttachments"
                >
                    새로고침
                </b-btn>
            </template>
        </admin-page-header>

        <!-- 대상 선택 -->
        <b-card class="mb-3">
            <b-row>
                <b-col cols="12" md="3" class="mb-3 mb-md-0">
                    <b-form-group label="기간" class="mb-0">
                        <b-form-select v-model="selectedPeriod" :disabled="loadingProjects"
                                       @change="onPeriodChange"
                        >
                            <b-form-select-option v-for="p in periods" :key="p" :value="p">
                                {{ p }}
                            </b-form-select-option>
                        </b-form-select>
                    </b-form-group>
                </b-col>
                <!-- 업체가 수백 개일 수 있어 검색해서 고른다(RemapExecuteTab 과 같은 컴포넌트) -->
                <b-col cols="12" md="5" class="mb-3 mb-md-0">
                    <b-form-group label="프로젝트 (캠페인)" label-for="df-project" class="mb-0">
                        <auto-complete-select id="df-project"
                                              :selected-id="selectedProjectCode"
                                              :options="projectOptions"
                                              option-value-key="id"
                                              :loading="loadingProjects"
                                              :disabled="!projectOptions.length"
                                              :placeholder="loadingProjects ? '불러오는 중...' : '프로젝트를 선택하세요'"
                                              list-max-height="360px"
                                              @input="onProjectInput"
                        />
                    </b-form-group>
                </b-col>
                <b-col cols="12" md="4">
                    <b-form-group label="업체" label-for="df-company" class="mb-0">
                        <auto-complete-select id="df-company"
                                              :selected-id="selectedCompanyKey"
                                              :options="companyOptions"
                                              option-value-key="id"
                                              :loading="loadingCompanies"
                                              :disabled="!companyOptions.length"
                                              :placeholder="companyPlaceholder"
                                              list-max-height="360px"
                                              @input="onCompanyInput"
                        />
                    </b-form-group>
                </b-col>
            </b-row>

            <!-- 프로젝트 전체 일괄 배치: 업체를 하나씩 고르지 않고, 프로젝트에 속한
                 모든 업체의 아직 추출되지 않은 첨부를 한 번에 배치 큐에 담는다
                 (지연·50% 할인). 업체 선택과 무관하게 프로젝트만 고르면 실행 가능. -->
            <div v-if="selectedProject"
                 class="mt-3 pt-3 border-top d-flex align-items-center flex-wrap">
                <div class="mr-auto small text-muted">
                    <b-icon-collection class="mr-1"/>
                    이 프로젝트에 속한 <strong>모든 업체</strong>의 아직 추출되지 않은 첨부를
                    배치(지연·최대 24h·50% 할인)로 한 번에 추출합니다.
                    <span v-if="companyOptions.length">· 업체 {{ companyOptions.length }}개</span>
                </div>
                <b-btn variant="outline-primary" size="sm" class="mt-2 mt-md-0"
                       :disabled="projectBatching"
                       @click="runProjectBatch"
                >
                    <b-spinner v-if="projectBatching" small class="mr-1"/>
                    프로젝트 전체 배치 추출
                </b-btn>
            </div>
        </b-card>

        <!-- 요약 + 실행 -->
        <b-card v-if="summary" class="mb-3">
            <div class="d-flex flex-wrap align-items-center justify-content-between">
                <div class="d-flex flex-wrap align-items-center">
                    <div class="mr-4 mb-2 mb-md-0">
                        <span class="text-muted small d-block">전체</span>
                        <strong>{{ summary.total }}건</strong>
                    </div>
                    <div class="mr-4 mb-2 mb-md-0">
                        <span class="text-muted small d-block">추출됨</span>
                        <strong class="text-success">{{ summary.extracted }}건</strong>
                    </div>
                    <div class="mr-4 mb-2 mb-md-0">
                        <span class="text-muted small d-block">미추출</span>
                        <strong :class="summary.pending ? 'text-danger' : ''">{{ summary.pending }}건</strong>
                    </div>
                    <div v-if="unlinkedCount" class="mr-4 mb-2 mb-md-0">
                        <span class="text-muted small d-block">연결 문항 없음</span>
                        <strong class="text-muted">{{ unlinkedCount }}건</strong>
                    </div>
                    <div class="mr-4 mb-2 mb-md-0">
                        <span class="text-muted small d-block">추출에 쓴 비용</span>
                        <strong>${{ round4(summary.extracted_cost) }}</strong>
                        <span class="text-muted small ml-1">(실측)</span>
                    </div>
                    <div class="mb-2 mb-md-0">
                        <span class="text-muted small d-block">미추출분 예상 비용</span>
                        <strong>~${{ summary.estimated_pending_cost }}</strong>
                        <span class="text-muted small ml-1">(대략치)</span>
                    </div>
                </div>
                <div class="d-flex align-items-center flex-wrap">
                    <b-form-checkbox v-model="forceRefresh" class="mr-3"
                                     :disabled="extracting || isBatchMode">
                        <span class="small">이미 추출된 것도 다시 추출</span>
                    </b-form-checkbox>
                    <!-- 추출 방식: 즉시(지금 바로) / 배치(지연·50% 할인). 대량 추출에만 적용. -->
                    <b-form-radio-group v-model="extractMode" class="mr-3" size="sm"
                                        buttons button-variant="outline-secondary"
                                        :disabled="extracting">
                        <b-form-radio value="immediate">즉시</b-form-radio>
                        <b-form-radio value="batch">배치(50% 할인)</b-form-radio>
                    </b-form-radio-group>
                    <b-btn :variant="isBatchMode ? 'outline-primary' : 'primary'"
                           :disabled="!selectedIds.length || extracting"
                           @click="runExtract"
                    >
                        <b-spinner v-if="extracting" small class="mr-1"/>
                        선택 {{ selectedIds.length }}건 {{ isBatchMode ? '배치 추출' : '추출' }}
                        <span v-if="!isBatchMode && selectedEstimatedCost > 0" class="small">
                            (~${{ selectedEstimatedCost }})
                        </span>
                        <span v-else-if="isBatchMode && selectedEstimatedCost > 0" class="small">
                            (~${{ round4(selectedEstimatedCost / 2) }} · 최대 24h 지연)
                        </span>
                    </b-btn>
                </div>
            </div>
        </b-card>

        <!-- 배치 대기 현황 + 바로 요청하기(전역) -->
        <!-- 대기 수는 pending_total(queued+preparing+prepared)을 쓴다. queued 만 세면 문서가
             전부 '준비 완료(prepared)'로 넘어간 순간 0 이 돼 이 안내줄·버튼이 사라진다(사각지대). -->
        <b-alert :show="batchPending > 0 || (batchQueue && batchQueue.in_batch_total > 0)"
                 variant="info" class="d-flex align-items-center flex-wrap"
        >
            <div class="mr-auto">
                <b-icon-hourglass-split class="mr-1"/>
                현재 배치 대기 <strong>{{ batchPending }}개</strong>
                <span v-if="batchQueue && batchQueue.in_batch_total > 0" class="text-muted">
                    (처리 중 {{ batchQueue.in_batch_total }}개)
                </span>
                <span class="text-muted small ml-1">
                    · 전체 대기가 {{ batchQueue && batchQueue.threshold }}개 이상 쌓이면 자동으로 처리됩니다.
                    지금 바로 처리하려면 오른쪽 버튼을 누르세요(전역).
                </span>
            </div>
            <b-btn size="sm" variant="primary" class="mt-2 mt-md-0"
                   :disabled="flushing || batchPending < 1"
                   @click="flushBatch"
            >
                <b-spinner v-if="flushing" small class="mr-1"/>
                바로 요청하기
            </b-btn>
        </b-alert>

        <!-- 목록 -->
        <b-card no-body>
            <!-- 필터 툴바: 연결문항 존재 여부 · 추출 상태 -->
            <div class="d-flex align-items-center flex-wrap px-3 pt-3">
                <div class="d-flex align-items-center mr-4 mb-2">
                    <span class="small text-muted mr-2">연결문항</span>
                    <b-form-radio-group v-model="linkedFilter" size="sm"
                                        buttons button-variant="outline-secondary">
                        <b-form-radio value="all">전체</b-form-radio>
                        <b-form-radio value="linked">연결됨</b-form-radio>
                        <b-form-radio value="unlinked">미연결</b-form-radio>
                    </b-form-radio-group>
                </div>
                <div class="d-flex align-items-center mr-4 mb-2">
                    <span class="small text-muted mr-2">추출 상태</span>
                    <b-form-radio-group v-model="statusFilter" size="sm"
                                        buttons button-variant="outline-secondary">
                        <b-form-radio value="all">전체</b-form-radio>
                        <b-form-radio value="extracted">추출됨</b-form-radio>
                        <b-form-radio value="pending">미추출</b-form-radio>
                        <b-form-radio value="extracting">추출중</b-form-radio>
                    </b-form-radio-group>
                </div>
                <div class="ml-auto mb-2 small text-muted">
                    {{ filteredAttachments.length }}건
                    <span v-if="filteredAttachments.length !== attachments.length">
                        / 전체 {{ attachments.length }}건
                    </span>
                </div>
            </div>

            <b-table :items="pagedAttachments" :fields="fields" :busy="loadingList"
                     responsive hover small show-empty
                     :tbody-tr-class="rowClass"
            >
                <!-- 조회 실패를 '첨부가 없습니다'로 보이지 않게 — 사유와 다시 시도 -->
                <template #empty>
                    <page-empty-component compact :text="emptyText" :error="loadError"
                                          @retry="reloadAttachments"
                    />
                </template>
                <template #table-busy>
                    <div class="text-center py-4">
                        <b-spinner class="align-middle mr-2"/>
                        <span>불러오는 중...</span>
                    </div>
                </template>

                <template #head(select)>
                    <b-form-checkbox :checked="allSelected" :indeterminate="someSelected"
                                     :disabled="!pagedAttachments.some(a => a.sub_codes.length)"
                                     title="연결 문항 없는 첨부는 개별로 선택하세요"
                                     @change="toggleAll"
                    />
                </template>
                <template #cell(select)="row">
                    <b-form-checkbox :checked="selectedIds.includes(row.item.storage_id)"
                                     @change="toggleOne(row.item.storage_id)"
                    />
                </template>

                <template #cell(filename)="row">
                    <b-link class="text-break" :disabled="downloadingId === row.item.storage_id"
                            title="원본 파일 다운로드"
                            @click="downloadOriginal(row.item)"
                    >
                        {{ row.item.filename }}
                        <b-spinner v-if="downloadingId === row.item.storage_id" small class="ml-1"/>
                    </b-link>
                    <span class="text-muted small d-block">{{ formatSize(row.item.file_size) }}</span>
                </template>

                <template #cell(sub_codes)="row">
                    <b-badge v-if="!row.item.sub_codes.length" variant="light" class="border text-muted">
                        연결 없음
                    </b-badge>
                    <template v-else>
                        <b-badge v-for="code in row.item.sub_codes.slice(0, 3)" :key="code"
                                 variant="light" class="mr-1 border"
                        >
                            {{ code }}
                        </b-badge>
                        <span v-if="row.item.sub_codes.length > 3" class="text-muted small">
                            외 {{ row.item.sub_codes.length - 3 }}개
                        </span>
                    </template>
                </template>

                <template #cell(extracted)="row">
                    <b-badge v-if="isExtracting(row.item)" variant="warning">
                        <b-spinner small class="mr-1" style="width: .7rem; height: .7rem"/>
                        {{ row.item.extracted ? '재추출 중' : '추출 중' }}
                    </b-badge>
                    <b-badge v-else :variant="row.item.extracted ? 'success' : 'secondary'">
                        {{ row.item.extracted ? '추출됨' : '미추출' }}
                    </b-badge>
                    <div v-if="row.item.extracted" class="text-muted small mt-1">
                        사실 {{ row.item.facts_count }}건
                        <span v-if="row.item.entries > 1">/ 조각 {{ row.item.entries }}</span>
                    </div>
                </template>

                <template #cell(model)="row">
                    <span v-if="row.item.model" class="small">{{ row.item.model }}</span>
                    <span v-else class="text-muted small">-</span>
                </template>

                <template #cell(cost)="row">
                    <span v-if="row.item.extracted" class="small">
                        ${{ round4(row.item.estimated_cost) }}
                    </span>
                    <span v-else class="text-muted small">~${{ row.item.estimated_extract_cost }}</span>
                </template>

                <template #cell(extracted_at)="row">
                    <span class="small">{{ formatDate(row.item.extracted_at) }}</span>
                </template>

                <template #cell(actions)="row">
                    <div class="text-nowrap">
                    <b-btn size="sm" variant="outline-secondary" class="mr-1"
                           :disabled="downloadingId === row.item.storage_id"
                           title="원본 파일 다운로드"
                           @click="downloadOriginal(row.item)"
                    >
                        원본
                        <b-spinner v-if="downloadingId === row.item.storage_id" small/>
                        <b-icon-download v-else class="ml-1"/>
                    </b-btn>
                    <b-btn v-if="row.item.extracted"
                           size="sm" variant="outline-primary" class="mr-1"
                           @click="openFacts(row.item)"
                    >
                        내용 보기
                    </b-btn>
                    <b-btn size="sm"
                           :variant="row.item.extracted ? 'outline-warning' : 'outline-success'"
                           :disabled="extracting || isExtracting(row.item)"
                           :title="row.item.extracted
                               ? '이 파일만 다시 추출 (기존 결과를 덮어씁니다)'
                               : '이 파일만 추출'"
                           @click="extractOne(row.item)"
                    >
                        <b-spinner v-if="isExtracting(row.item)" small class="mr-1"/>
                        <b-icon-arrow-clockwise v-else-if="row.item.extracted" class="mr-1"/>
                        {{ row.item.extracted ? '재추출' : '추출' }}
                    </b-btn>
                    </div>
                </template>
            </b-table>

            <!-- 페이지네이션 — 필터 적용된 전체 기준 -->
            <div v-if="filteredAttachments.length > perPage"
                 class="d-flex justify-content-center py-3">
                <b-pagination v-model="currentPage" :total-rows="filteredAttachments.length"
                              :per-page="perPage" align="center" size="sm" class="mb-0"
                              @change="onPageChange"
                />
            </div>
        </b-card>

        <!-- facts 모달 -->
        <b-modal v-model="factsModal" size="xl" scrollable ok-only ok-title="닫기"
                 :title="factsTitle"
        >
            <div v-if="factsLoading" class="text-center py-4">
                <b-spinner/>
            </div>
            <div v-else-if="!factsData || !factsData.entries.length" class="text-muted">
                추출된 사실이 없습니다.
            </div>
            <div v-else>
                <div v-for="(entry, ei) in factsData.entries" :key="ei" class="mb-4">
                    <div class="d-flex align-items-center mb-2">
                        <strong class="mr-2">{{ entry.inner_file_key }}</strong>
                        <b-badge variant="light" class="border mr-1">{{ entry.model }}</b-badge>
                        <b-badge v-if="entry.language" variant="light" class="border mr-1">
                            {{ entry.language }}
                        </b-badge>
                        <span class="text-muted small">
                            in {{ entry.input_tokens }} / out {{ entry.output_tokens }}
                            · ${{ round4(entry.estimated_cost) }}
                        </span>
                    </div>
                    <b-table-simple small bordered responsive class="mb-0">
                        <b-thead>
                            <b-tr>
                                <b-th style="width: 120px">위치</b-th>
                                <b-th>내용</b-th>
                            </b-tr>
                        </b-thead>
                        <b-tbody>
                            <b-tr v-for="(fact, fi) in entry.facts" :key="fi">
                                <b-td class="text-muted small">{{ fact.page_or_location || '-' }}</b-td>
                                <b-td class="small text-break" style="white-space: pre-wrap">{{ fact.content }}</b-td>
                            </b-tr>
                        </b-tbody>
                    </b-table-simple>
                </div>
            </div>
        </b-modal>
    </b-container>
</template>

<script>
import { getErrorMessage } from '@core/utils/api/errorMessage';
import AutoCompleteSelect from '@core/components/common/AutoCompleteSelect.vue';
import PageEmptyComponent from '@shared_components/admin/components/common/PageEmptyComponent.vue';
import { isSameQuery } from '@shared_mixins/contractor-dashboard/contractor-list-query';

const LINKED_FILTERS = ['all', 'linked', 'unlinked'];
const STATUS_FILTERS = ['all', 'extracted', 'pending', 'extracting'];

export default {
    name: 'DocumentFacts',
    components: {AutoCompleteSelect, PageEmptyComponent},
    data() {
        return {
            // GET /assessments/periods(내림차순)로 채운다 — 기본은 첫 값(최신 기간).
            periods: [],
            selectedPeriod: null,
            projects: [],
            // auto-complete-select 는 id 로 고른다 — 객체는 computed(selectedProject/selectedCompany)로 파생.
            selectedProjectCode: null,
            companies: [],
            selectedCompanyKey: null,
            loadError: '',
            attachments: [],
            summary: null,
            selectedIds: [],
            forceRefresh: false,
            // 추출 방식(요구 옵션): 즉시(동기, 지금 바로) / 배치(벤더 Batch API·지연·50% 할인).
            // 기본은 즉시 — 기존 동작 유지. 배치는 대량 추출 버튼에만 적용한다(행 개별 추출은
            // '지금 이거 하나' 라는 의도라 항상 즉시).
            extractMode: 'immediate',
            loadingProjects: false,
            loadingCompanies: false,
            loadingList: false,
            extracting: false,
            factsModal: false,
            factsLoading: false,
            factsData: null,
            factsTitle: '',
            downloadingId: null,
            // 필터: 연결문항 존재 여부(all|linked|unlinked), 추출 상태(all|extracted|pending|extracting).
            // 연결 문항이 없는 첨부는 어떤 판정에도 쓰이지 않아 추출해도 비용만 나간다.
            linkedFilter: 'all',
            statusFilter: 'all',
            // 페이지네이션 — 필터 적용된 목록을 페이지 단위로 나눈다.
            currentPage: 1,
            perPage: 20,
            // 전역 배치 대기 현황(목록 API 가 실어줌). {queued_total, in_batch_total, threshold}
            batchQueue: null,
            flushing: false,
            // 프로젝트 전체 일괄 배치 요청 진행 플래그(중복 클릭 방지).
            projectBatching: false,
            // 추출 요청을 보낸 storage — 캐시 행이 생길 때까지 서버는 계속
            // '미추출' 로 응답하므로, 그 사이 무엇이 도는지 화면에서 보이게 한다.
            extractingIds: [],
            // 재추출(force_refresh) 요청 시점의 값 스냅샷.
            // 재추출은 이미 extracted=true 인 행을 UPDATE 하므로 'extracted 여부'
            // 로는 완료를 알 수 없고, created_at 도 갱신되지 않는다(서버가 기존
            // 행을 update 만 함) — 값이 바뀌었는지로 판정한다.
            extractSnapshots: {},
            pollTimer: null,
            pollCount: 0,
            // 폴링이 한도(10분)나 연속 실패로 멈췄는데 '10초마다 자동 갱신'이 남지 않게 한다.
            pollFailures: 0,
            pollingStopped: false,
            initializing: true,
            // 주소가 연달아 바뀌면(뒤로가기 연타) 늦게 끝난 복원이 새 복원을 덮지 않게 한다.
            initToken: 0,
            projectsRequest: 0,
            fields: [
                {key: 'select', label: '', thStyle: {width: '40px'}},
                {key: 'filename', label: '파일', thStyle: {width: '260px'}},
                {key: 'sub_codes', label: '연결 문항'},
                {key: 'extracted', label: '추출 상태'},
                {key: 'model', label: '모델'},
                {key: 'cost', label: '비용'},
                {key: 'extracted_at', label: '추출 일시'},
                // 버튼 3개(원본 / 내용 보기 / 추출·재추출)가 한 줄에 들어가야 한다
                {key: 'actions', label: '', thStyle: {width: '270px'}},
            ],
        };
    },
    computed: {
        projectOptions() {
            const seen = new Set();
            const out = [];
            for (const group of this.projects || []) {
                for (const p of (group.projects || [group])) {
                    const code = p.project_code;
                    if (!code || seen.has(code)) continue;
                    seen.add(code);
                    const label = p.project_name || p.name || code;
                    out.push({id: code, text: label === code ? code : `${label} (${code})`, project_code: code, label});
                }
            }
            return out.sort((a, b) => a.label.localeCompare(b.label));
        },
        companyOptions() {
            return (this.companies || [])
                .map(c => {
                    const campaignId = this.pick(c, 'campaign_id');
                    const companyId = this.pick(c, 'company_id');
                    const name = this.pick(c, 'company_name');
                    return {
                        id: `${campaignId}-${companyId}`,
                        text: name ? `${name} (${companyId})` : `${companyId}`,
                        campaign_id: campaignId,
                        company_id: companyId,
                        label: `${name || companyId}`,
                    };
                })
                .filter(c => c.campaign_id && c.company_id);
        },
        selectedProject() {
            return this.projectOptions.find(p => p.id === this.selectedProjectCode) || null;
        },
        selectedCompany() {
            return this.companyOptions.find(c => c.id === this.selectedCompanyKey) || null;
        },
        // 배치 화면 왕복에 싣는 선택 키 — 필터·페이지는 싣지 않는다(배치 화면의 page 와 겹친다).
        selectionQuery() {
            const query = {};
            if (this.selectedPeriod) query.period = this.selectedPeriod;
            if (this.selectedProject) query.project_code = this.selectedProject.project_code;
            if (this.selectedCompany) {
                query.campaign_id = String(this.selectedCompany.campaign_id);
                query.company_id = String(this.selectedCompany.company_id);
            }
            return query;
        },
        // 화면 상태 그대로의 주소 — 기본값(전체·1페이지)은 생략한다.
        stateQuery() {
            const query = {...this.selectionQuery};
            if (this.linkedFilter !== 'all') query.linked = this.linkedFilter;
            if (this.statusFilter !== 'all') query.extract_status = this.statusFilter;
            if (this.currentPage > 1) query.page = String(this.currentPage);
            return query;
        },
        companyPlaceholder() {
            if (this.loadingCompanies) return '불러오는 중...';
            if (!this.selectedProject) return '프로젝트를 먼저 선택하세요';
            return this.companyOptions.length ? '업체를 선택하세요' : '업체가 없습니다';
        },
        canReload() {
            return !!(this.selectedCompany && this.selectedCompany.campaign_id);
        },
        // 헤더의 'N건 추출 중' — 요청 직후 힌트(extractingIds)와 서버 status 를 함께 센다(새로고침 뒤에도 보이게).
        extractingCount() {
            return this.attachments.filter(a => this.isExtracting(a)).length;
        },
        unlinkedCount() {
            return this.attachments.filter(a => !a.sub_codes.length).length;
        },
        // 필터(연결문항·추출상태) 적용 + 정렬된 전체 목록. 페이지네이션 전 단계.
        filteredAttachments() {
            const rows = this.attachments.filter(a => {
                const linked = a.sub_codes.length > 0;
                if (this.linkedFilter === 'linked' && !linked) return false;
                if (this.linkedFilter === 'unlinked' && linked) return false;
                if (this.statusFilter !== 'all'
                    && this.attachmentStatus(a) !== this.statusFilter) return false;
                return true;
            });
            // 연결 문항 있는 것 먼저 — 실제로 판정에 쓰이는 파일이 위로.
            // 같은 그룹 안에서는 미추출 우선(할 일이 위에 오게).
            return rows.sort((a, b) => {
                const linked = (b.sub_codes.length > 0) - (a.sub_codes.length > 0);
                if (linked) return linked;
                return (a.extracted === b.extracted) ? 0 : (a.extracted ? 1 : -1);
            });
        },
        // 현재 페이지에 보이는 행만 — b-table :items 이자 '전체 선택' 대상.
        pagedAttachments() {
            const start = (this.currentPage - 1) * this.perPage;
            return this.filteredAttachments.slice(start, start + this.perPage);
        },
        emptyText() {
            if (!this.selectedCompany) return '캠페인과 업체를 선택하면 첨부 목록이 표시됩니다.';
            if (this.attachments.length) {
                return '조건에 맞는 첨부가 없습니다. (필터를 조정해 보세요)';
            }
            return '첨부가 없습니다.';
        },
        // 전체 선택은 '현재 페이지에 보이는 행'만 기준으로 한다(필터·페이지 적용 후).
        // 연결 문항이 없는 첨부는 어떤 판정에도 쓰이지 않으므로 전체 선택에서 뺀다(개별 체크는 가능).
        allSelected() {
            const ids = this.pagedAttachments.filter(a => a.sub_codes.length).map(a => a.storage_id);
            return ids.length > 0 && ids.every(id => this.selectedIds.includes(id));
        },
        someSelected() {
            const ids = this.pagedAttachments.filter(a => a.sub_codes.length).map(a => a.storage_id);
            const picked = ids.filter(id => this.selectedIds.includes(id)).length;
            return picked > 0 && picked < ids.length;
        },
        selectedEstimatedCost() {
            const sum = this.attachments
                .filter(a => this.selectedIds.includes(a.storage_id) && !a.extracted)
                .reduce((acc, a) => acc + (a.estimated_extract_cost || 0), 0);
            return Math.round(sum * 10000) / 10000;
        },
        isBatchMode() {
            return this.extractMode === 'batch';
        },
        // 제출 전 처리 중(대기+준비중+준비완료) 총량. 서버 pending_total 을 쓰되, 롤링 배포 중
        // 구 backend(pending_total 없음) 응답은 queued_total 로 폴백한다.
        batchPending() {
            const q = this.batchQueue;
            if (!q) return 0;
            return (q.pending_total != null ? q.pending_total : (q.queued_total || 0));
        },
    },
    watch: {
        extractMode(mode) {
            // 배치는 force_refresh(즉시성 기대)를 지원하지 않는다 — 전환 시 해제한다.
            if (mode === 'batch') this.forceRefresh = false;
        },
        linkedFilter() { this.onFilterChange(); },
        statusFilter() { this.onFilterChange(); },
        // 같은 화면에서 주소만 바뀌면(메뉴 재클릭·뒤로가기) 컴포넌트가 재사용되므로 그 주소대로 다시 맞춘다.
        // 화면이 스스로 쓴 주소(stateQuery 와 같음)는 무시한다.
        '$route.query'(query) {
            if (isSameQuery(query || {}, this.stateQuery)) return;
            this.stopPolling();
            this.extractingIds = [];
            this.extractSnapshots = {};
            this.resetList();
            this.initFromQuery();
        },
    },
    created() {
        this.initFromQuery();
    },
    beforeDestroy() {
        this.initToken += 1;
        this.stopPolling();
    },
    methods: {
        // ── query-parameter 로 랜딩 상태 복원 (페이지 이동/뒤로가기 안전) ──
        // 추출·배치 요청은 주소로 실행하지 않는다 — 조회 상태만 복원한다.
        async initFromQuery() {
            const token = ++this.initToken;
            const stale = () => token !== this.initToken;
            this.initializing = true;
            const q = this.$route.query || {};
            // 크래프트된 링크를 신뢰하지 않는다 — vue-router 는 중복 키를 배열로
            // 파싱하므로 모든 값을 스칼라로 강제하고 화이트리스트/숫자로 검증한다.
            const first = v => (Array.isArray(v) ? v[0] : v);
            const asStr = v => (typeof first(v) === 'string' ? first(v) : '');
            const asInt = v => {
                const n = Math.floor(Number(first(v)));
                return Number.isFinite(n) && n > 0 ? n : null;
            };
            const period = asStr(q.period);
            const pendingProject = asStr(q.project_code) || null;
            const pendingCampaign = asInt(q.campaign_id);
            const pendingCompany = asInt(q.company_id);
            const pendingPage = asInt(q.page);
            // 필터는 첫 await 전에 넣는다 — watcher 가 복원 중(initializing)에 돌아 URL·페이지를 건드리지 않게.
            const linked = asStr(q.linked);
            const status = asStr(q.extract_status);
            this.linkedFilter = LINKED_FILTERS.includes(linked) ? linked : 'all';
            this.statusFilter = STATUS_FILTERS.includes(status) ? status : 'all';
            this.selectedProjectCode = null;
            this.selectedCompanyKey = null;
            this.companies = [];
            // 링크가 가리키는 대상을 못 찾으면 알리고 그 키와 하위 키를 주소에서 지운다(replace).
            let dropped = false;
            const warnMissing = (target) => {
                dropped = true;
                this.$EventBus.$emit('toast-warning', '링크 확인', `링크의 ${target} 찾을 수 없습니다. 다시 선택해 주세요.`);
            };
            try {
                if (!this.periods.length) await this.fetchPeriods();
                if (stale()) return;
                const periodFound = !period || this.periods.includes(period);
                if (!periodFound && this.periods.length) warnMissing(`기간(${period})을`);
                this.selectedPeriod = period && periodFound ? period : (this.periods[0] || null);
                if (!this.selectedPeriod) return;
                const projectsLoaded = await this.fetchProjects();
                if (stale()) return;
                // 목록 조회 자체가 실패했으면(토스트 안내됨) '못 찾음'으로 지우지 않고 주소를 그대로 둔다.
                if (!periodFound || !pendingProject || !projectsLoaded) return;
                if (!this.projectOptions.some(p => p.id === pendingProject)) {
                    warnMissing('프로젝트를');
                    return;
                }
                this.selectedProjectCode = pendingProject;
                const companiesLoaded = await this.loadCompanies();
                if (stale() || !pendingCampaign || !pendingCompany || !companiesLoaded) return;
                const key = `${pendingCampaign}-${pendingCompany}`;
                if (!this.companyOptions.some(c => c.id === key)) {
                    warnMissing('업체를');
                    return;
                }
                this.selectedCompanyKey = key;
                await this.loadAttachments();
                if (stale()) return;
                // 페이지는 목록을 받은 뒤 적용한다 — 범위 밖이면 1페이지로 두고 주소를 고친다.
                if (pendingPage && !this.loadError) {
                    const pages = Math.ceil(this.filteredAttachments.length / this.perPage);
                    if (pendingPage <= pages) this.currentPage = pendingPage;
                    else dropped = true;
                }
            } finally {
                if (!stale()) {
                    // 위에서 바꾼 필터의 watcher 가 복원 중으로 판정되도록 한 틱 기다린 뒤 해제한다.
                    await this.$nextTick();
                    this.initializing = false;
                    if (dropped) this.syncQuery(false);
                }
            }
        },
        // 사용자 조작(기간·프로젝트·업체 선택, 필터, 페이지)은 push — 뒤로가기로 이전 선택에 돌아간다.
        // 복원 중 보정(못 찾은 키 제거·범위 밖 페이지)은 replace.
        syncQuery(push = true) {
            if (this.initializing) return;
            if (isSameQuery(this.$route.query || {}, this.stateQuery)) return;
            this.$router[push ? 'push' : 'replace']({query: this.stateQuery}).catch(() => {});
        },
        pick(obj, key) {
            if (!obj) return null;
            return obj[key] !== undefined ? obj[key] : obj[key.replace(/_([a-z])/g, (m, c) => c.toUpperCase())];
        },
        round4(v) {
            return Math.round((v || 0) * 10000) / 10000;
        },
        formatSize(bytes) {
            if (!bytes) return '-';
            const mb = bytes / 1024 / 1024;
            return mb >= 1 ? `${mb.toFixed(1)}MB` : `${Math.round(bytes / 1024)}KB`;
        },
        formatDate(iso) {
            if (!iso) return '-';
            return iso.replace('T', ' ').slice(0, 16);
        },
        async fetchPeriods() {
            try {
                const {data} = await this.$axios.get('/assessments/periods');
                this.periods = (Array.isArray(data) ? data : []).filter(p => typeof p === 'string' && p);
            } catch (e) {
                if (e && e.response && e.response.status === 401) return;
                this.$EventBus.$emit('toast-failure', '조회 오류',
                    getErrorMessage(e, '기간 목록을 불러오지 못했습니다. 화면을 새로고침해 주세요.'));
            }
        },
        // 성공 여부를 돌려준다 — 실패를 '링크의 프로젝트 없음'으로 오인하지 않게.
        // 기간을 연달아 바꾸면 늦게 온 이전 기간 응답이 새 목록을 덮지 않도록 마지막 요청만 반영한다.
        async fetchProjects() {
            const request = ++this.projectsRequest;
            const stale = () => request !== this.projectsRequest;
            this.loadingProjects = true;
            try {
                // /projects 는 per_page 상한(<=100)이 있어 페이지를 끝까지 수집한다.
                const PER_PAGE = 100;
                const items = [];
                let page = 1;
                let pages = 1;
                let total = Infinity;
                for (;;) {
                    const {data} = await this.$axios.get('/projects', {
                        params: {period: this.selectedPeriod, page, per_page: PER_PAGE},
                    });
                    if (stale()) return false;
                    const pageItems = (data && data.items) || [];
                    items.push(...pageItems);
                    pages = (data && data.pages) || page;
                    if (data && typeof data.total === 'number') total = data.total;
                    if (pageItems.length === 0 || items.length >= total || page >= pages) break;
                    page += 1;
                }
                this.projects = items;
                return true;
            } catch (e) {
                if (stale()) return false;
                this.projects = [];
                if (e && e.response && e.response.status === 401) return false;
                this.$EventBus.$emit('toast-failure', '조회 오류', getErrorMessage(e, '프로젝트 목록을 불러오지 못했습니다.'));
                return false;
            } finally {
                if (!stale()) this.loadingProjects = false;
            }
        },
        onPeriodChange() {
            this.selectedProjectCode = null;
            this.companies = [];
            this.selectedCompanyKey = null;
            this.resetList();
            this.syncQuery();
            this.fetchProjects();
        },
        // auto-complete-select 는 같은 항목을 다시 골라도 input 을 보낸다 — 바뀐 경우만 처리.
        onProjectInput(code) {
            if (code === this.selectedProjectCode) return;
            this.selectedProjectCode = code;
            this.selectedCompanyKey = null;
            this.companies = [];
            this.resetList();
            this.syncQuery();
            this.loadCompanies();
        },
        onCompanyInput(key) {
            if (key === this.selectedCompanyKey) return;
            this.selectedCompanyKey = key;
            this.resetList();
            this.syncQuery();
            this.loadAttachments();
        },
        async loadCompanies() {
            if (!this.selectedProject) return false;
            const code = this.selectedProject.project_code;
            this.loadingCompanies = true;
            try {
                const {data} = await this.$axios.get('/assessments/results', {
                    params: {project_code: code},
                });
                // 응답 전에 다른 프로젝트로 바꿨으면 버린다.
                if (!this.selectedProject || this.selectedProject.project_code !== code) return false;
                this.companies = Array.isArray(data) ? data : [];
                return true;
            } catch (e) {
                if (e && e.response && e.response.status === 401) return false;
                this.$EventBus.$emit('toast-failure', '조회 오류', getErrorMessage(e, '업체 목록을 불러오지 못했습니다.'));
                return false;
            } finally {
                this.loadingCompanies = false;
            }
        },
        resetList() {
            this.attachments = [];
            this.summary = null;
            this.selectedIds = [];
            this.currentPage = 1;
            this.loadError = '';
        },
        async loadAttachments(silent = false) {
            if (!this.canReload) return;
            // silent(폴링) 갱신은 스피너·선택 초기화·URL 갱신·페이지 리셋 없이 데이터만
            // 조용히 교체한다 — 10초 주기 폴링이 화면을 요란하게 다시 그리지 않게 한다.
            if (!silent) {
                this.loadingList = true;
                this.resetList();   // 이전 업체 목록이 새 업체 선택 아래 남지 않게
            }
            const targetKey = this.selectedCompanyKey;
            try {
                const {data} = await this.$axios.get('/document-facts', {
                    params: {
                        campaign_id: this.selectedCompany.campaign_id,
                        company_id: this.selectedCompany.company_id,
                    },
                });
                // 응답 전에 업체를 바꿨으면 이전 업체 목록으로 덮지 않는다.
                if (targetKey !== this.selectedCompanyKey) return;
                this.attachments = (data && data.attachments) || [];
                this.summary = (data && data.summary) || null;
                this.batchQueue = (data && data.batch_queue) || null;
                this.loadError = '';
                this.pollFailures = 0;
                if (!silent) {
                    this.currentPage = 1;   // 사용자 조회만 1페이지로(폴링은 페이지 유지)
                    this.pollingStopped = false;
                }
                this.reconcileExtracting();
                // 새로고침으로 client hint 가 비어도 서버가 진행중이면 폴링을 재개한다.
                // silent 응답(멈춘 뒤 늦게 온 폴링 응답 포함)은 재개하지 않는다 — 10분 한도·중단 표시가 풀리지 않게.
                if (!silent && this.hasActiveExtractions()) this.startPolling();
            } catch (e) {
                if (targetKey !== this.selectedCompanyKey) return;
                if (e && e.response && e.response.status === 401) return;
                if (silent) {
                    // 폴링 실패 한두 번은 다음 주기에 재시도(화면 깜빡임 방지). 연속 3회면 멈추고 알린다.
                    this.pollFailures += 1;
                    if (this.pollFailures >= 3 && this.pollTimer) {
                        this.stopPolling();
                        this.pollingStopped = true;
                        this.$EventBus.$emit('toast-warning', '자동 갱신 중단',
                            '목록을 3번 연속 불러오지 못해 자동 갱신을 멈췄습니다. [새로고침]으로 확인하세요.');
                    }
                    return;
                }
                const msg = getErrorMessage(e, '첨부 목록을 불러오지 못했습니다.');
                this.$EventBus.$emit('toast-failure', '조회 오류', msg);
                this.resetList();
                this.loadError = msg;
            } finally {
                if (!silent) this.loadingList = false;
            }
        },
        // 새로고침·다시 시도·요청 뒤 재조회 — 1페이지로 돌아가므로 주소의 page 도 맞춘다(replace).
        reloadAttachments() {
            this.loadAttachments();
            this.syncQuery(false);
        },
        onPageChange(page) {
            this.currentPage = page;
            this.syncQuery();
        },
        // 서버가 주는 진행 상태(refresh-safe)의 source of truth. status 를 안 주는
        // 구 backend(롤링 배포) 응답은 extracted 로 폴백한다.
        serverAttachmentStatus(item) {
            if (item && ['pending', 'extracting', 'extracted'].includes(item.status)) {
                return item.status;
            }
            return item && item.extracted ? 'extracted' : 'pending';
        },
        isExtracting(item) {
            // extracted 여부를 조건에 넣지 않는다 — 재추출은 이미 extracted=true 인
            // 행을 덮어쓰므로, 넣으면 재추출 중에 배지가 사라진다.
            // 완료 제거는 reconcileExtracting 이 값 변경으로 판정한다.
            // 서버 status(refresh-safe)를 우선하고, extractingIds 는 POST 직후 보조 힌트.
            return (
                this.serverAttachmentStatus(item) === 'extracting'
                || this.extractingIds.includes(item.storage_id)
            );
        },
        // 필터·배지와 동일한 추출 상태 분류: extracting > extracted > pending.
        attachmentStatus(item) {
            return this.isExtracting(item)
                ? 'extracting'
                : this.serverAttachmentStatus(item);
        },
        onFilterChange() {
            // 주소에서 복원하는 중이면 그 주소의 페이지를 지키고 주소도 다시 쓰지 않는다.
            if (this.initializing) return;
            // 필터가 바뀌면 1페이지로 돌아가고, 필터에서 빠진 항목은 선택 해제한다
            // (숨겨진 항목이 몰래 추출되지 않도록).
            this.currentPage = 1;
            const visible = new Set(this.filteredAttachments.map(a => a.storage_id));
            this.selectedIds = this.selectedIds.filter(id => visible.has(id));
            this.syncQuery();
        },
        // '바로 요청하기' — 임계치 안 기다리고 지금 쌓인 배치를 즉시 처리 트리거(전역).
        async flushBatch() {
            if (this.flushing) return;
            const ok = await this.$showModal({
                type: 'confirm',
                state: 'warning',
                title: '배치 바로 요청 - AI 비용 발생',
                message: `전역 배치 대기 ${this.batchPending}개(다른 업체·다른 관리자 몫 포함)를 지금 바로 제출합니다.`,
            });
            if (!ok) return;
            this.flushing = true;
            try {
                const {data} = await this.$axios.post('/document-facts/batch/flush');
                const n = (data && data.queued_total) || 0;
                this.$EventBus.$emit(
                    'toast-success', '배치 처리를 시작했습니다',
                    `대기 중이던 배치 ${n}건을 지금 처리합니다. 완료까지 시간이 걸릴 수 있으며, `
                    + '목록 재조회로 확인하세요.',
                );
                this.reloadAttachments();
            } catch (e) {
                const msg = getErrorMessage(e, '배치 처리 요청에 실패했습니다.');
                this.$EventBus.$emit('toast-failure', '요청 실패', msg);
            } finally {
                this.flushing = false;
            }
        },
        // '프로젝트 전체 배치 추출' — 프로젝트 소속 전 업체의 미추출 첨부를 한 번에
        // 배치 큐에 담는다. 서버가 이미 추출됐거나 진행 중인 것은 제외하므로,
        // 프론트는 project_code 만 넘기고 결과 요약(업체 수·접수·제외)을 안내한다.
        async runProjectBatch() {
            if (!this.selectedProject || this.projectBatching) return;
            const code = this.selectedProject.project_code;
            const label = this.selectedProject.label || code;
            const n = this.companyOptions.length;
            const ok = await this.$bvModal.msgBoxConfirm(
                `[${label}] 프로젝트에 속한 모든 업체${n ? ` (${n}개)` : ''}의 `
                + '아직 추출되지 않은 첨부 전체를 배치로 추출합니다. '
                + '지연·최대 24시간이 걸릴 수 있고 50% 할인이 적용됩니다. 진행할까요?',
                {
                    title: '프로젝트 전체 배치 추출',
                    okTitle: '배치 추출', cancelTitle: '취소',
                    okVariant: 'primary', centered: true,
                },
            ).catch(() => false);
            if (!ok) return;
            this.projectBatching = true;
            try {
                const {data} = await this.$axios.post('/document-facts/extract/project', {
                    project_code: code,
                });
                const companies = (data && data.companies) || 0;
                const queued = (data && data.queued) || 0;
                const extracted = (data && data.already_extracted) || 0;
                const active = (data && data.already_queued) || 0;
                const errs = (data && data.errors && data.errors.length) || 0;
                this.$EventBus.$emit(
                    'toast-success', '프로젝트 배치 대기열에 담김',
                    `업체 ${companies}개 대상 · 새로 ${queued}건 접수`
                    + `${extracted ? ` · 이미 추출 ${extracted}건 제외` : ''}`
                    + `${active ? ` · 진행 중 ${active}건 제외` : ''}`
                    + `${errs ? ` · 일부 업체 처리 실패 ${errs}건` : ''}. `
                    + '완료까지 최대 24시간 걸릴 수 있으며, 목록 재조회로 확인하세요.',
                );
                // 현재 업체가 선택돼 있으면 목록·배치 대기 현황을 조용히 갱신한다.
                if (this.canReload) this.loadAttachments(true);
            } catch (e) {
                const msg = getErrorMessage(e, '프로젝트 배치 요청에 실패했습니다.');
                this.$EventBus.$emit('toast-failure', '요청 실패', msg);
            } finally {
                this.projectBatching = false;
            }
        },
        rowClass(item) {
            if (!item) return '';
            // 추출 중인 행을 먼저 강조 — 지금 무엇이 도는지가 가장 궁금한 정보
            if (this.isExtracting(item)) return 'table-warning';
            // 연결 문항 없는 행은 흐리게 — 추출 대상이 아님을 시각적으로 구분
            return !item.sub_codes.length ? 'text-muted bg-light' : '';
        },
        toggleAll(checked) {
            // 전체 선택은 '현재 페이지에 보이는 행'만 대상으로 한다(필터·페이지 적용 후).
            // 다른 페이지에서 개별 선택한 항목은 그대로 유지한다.
            const pageIds = this.pagedAttachments.filter(a => a.sub_codes.length).map(a => a.storage_id);
            if (checked) {
                this.selectedIds = [...new Set([...this.selectedIds, ...pageIds])];
            } else {
                const pageSet = new Set(pageIds);
                this.selectedIds = this.selectedIds.filter(id => !pageSet.has(id));
            }
        },
        toggleOne(id) {
            const i = this.selectedIds.indexOf(id);
            if (i >= 0) this.selectedIds.splice(i, 1);
            else this.selectedIds.push(id);
        },
        // 행에서 바로 실행 — 체크 → 상단 토글 → 실행 3단계를 거치지 않게 한다.
        // 이미 추출된 파일이면 force_refresh 로 보낸다(그 버튼의 의미가 '재추출').
        // 개별 추출은 '지금 이거 하나' 라는 의도라 배치 토글과 무관하게 항상 즉시.
        async extractOne(item) {
            if (item.extracted) {
                const ok = await this.$showModal({
                    type: 'confirm',
                    state: 'warning',
                    title: '재추출 확인 - AI 비용 발생',
                    message: `${item.filename} 의 기존 추출 결과를 덮어씁니다. 재추출은 다시 과금되며 되돌릴 수 없습니다.`,
                });
                if (!ok) return;
            }
            await this.postExtract([item.storage_id], item.extracted, 'immediate');
        },
        async runExtract() {
            if (!this.selectedIds.length) return;
            const isBatch = this.isBatchMode;
            const picked = this.attachments.filter(a => this.selectedIds.includes(a.storage_id));
            const overwrite = !isBatch && this.forceRefresh ? picked.filter(a => a.extracted).length : 0;
            const unlinked = picked.filter(a => !a.sub_codes.length).length;
            // 서버는 이미 추출된 첨부의 estimated_extract_cost 를 0 으로 준다 — 금액은 미추출분만 합산한다.
            const targets = picked.filter(a => !a.extracted);
            const skipped = overwrite ? 0 : picked.length - targets.length;
            const count = overwrite ? picked.length : targets.length;
            const cost = targets.reduce((acc, a) => acc + (a.estimated_extract_cost || 0), 0) / (isBatch ? 2 : 1);
            const h = this.$createElement;
            const ok = await this.$showModal({
                type: 'confirm',
                state: 'warning',
                title: `첨부 추출 (${isBatch ? '배치' : '즉시'}) - AI 비용 발생`,
                message: h('div', {}, [
                    h('div', {}, `${this.selectedCompany.label} 업체의 첨부 ${count}건을 추출합니다(미추출분 예상 비용 약 $${Math.round(cost * 10000) / 10000}).`),
                    skipped > 0 && h('div', {class: 'text-muted small mt-1'}, `이미 추출된 ${skipped}건은 건너뜁니다.`),
                    unlinked > 0 && h('div', {class: 'text-danger mt-2'},
                        `연결 문항이 없는 ${unlinked}건이 포함되어 있습니다. 판정에 쓰이지 않고 비용만 나갑니다.`),
                    overwrite > 0 && h('div', {class: 'text-danger mt-2'},
                        `이미 추출된 ${overwrite}건의 기존 결과를 덮어씁니다. 이 재추출 비용은 위 금액에 포함되지 않으며, 되돌릴 수 없습니다.`),
                ]),
            });
            if (!ok) return;
            await this.postExtract(this.selectedIds, this.forceRefresh, this.extractMode);
        },
        async postExtract(storageIds, forceRefresh, mode = 'immediate') {
            if (!storageIds.length || this.extracting) return;
            const isBatch = mode === 'batch';
            this.extracting = true;
            try {
                const {data} = await this.$axios.post('/document-facts/extract', {
                    company_id: this.selectedCompany.company_id,
                    storage_ids: storageIds,
                    // 배치는 force_refresh(즉시성 기대)를 지원하지 않는다 — 항상 false.
                    force_refresh: isBatch ? false : !!forceRefresh,
                    mode,
                });
                const queued = (data && data.queued) || storageIds.length;
                if (isBatch || (data && data.mode === 'batch')) {
                    // 배치는 벤더에 지연 제출(최대 24h)이라 '추출 중' 폴링을 돌리지 않는다.
                    // 큐에 담겼음을 안내하고 현재 목록만 한 번 갱신한다(나중에 재조회로 확인).
                    const skipped = (data && data.skipped) || 0;
                    this.selectedIds = [];
                    this.$EventBus.$emit(
                        'toast-success', '배치 추출 대기열에 담김',
                        `${queued}건을 배치(지연·50% 할인)로 처리합니다`
                        + `${skipped ? ` (이미 대기 중 ${skipped}건 제외)` : ''}. `
                        + '완료까지 최대 24시간 걸릴 수 있으며, 목록 재조회로 확인하세요.',
                    );
                    this.reloadAttachments();
                    return;
                }
                // POST 직후 다음 GET 전까지만 optimistic hint 로 쓴다.
                // 이후 상태의 source of truth 는 서버의 attachment.status 다.
                const byId = {};
                this.attachments.forEach(a => { byId[a.storage_id] = a; });
                storageIds.forEach(id => {
                    const cur = byId[id];
                    // 재추출은 '값이 바뀌었는가'로만 완료를 알 수 있어 지금 값을 남긴다
                    if (forceRefresh && cur && cur.extracted) {
                        this.$set(this.extractSnapshots, id, this.factsSignature(cur));
                    }
                });
                const acceptedIds = (data && data.storage_ids) || storageIds;
                const ids = new Set([...this.extractingIds, ...acceptedIds]);
                this.extractingIds = [...ids];
                this.selectedIds = [];
                this.startPolling();
                this.$EventBus.$emit(
                    'toast-success', '추출 요청됨',
                    `${queued}건을 백그라운드에서 추출합니다. 완료되면 목록이 자동으로 갱신됩니다.`,
                );
            } catch (e) {
                const msg = getErrorMessage(e, '추출 요청에 실패했습니다.');
                this.$EventBus.$emit('toast-failure', '요청 실패', msg);
            } finally {
                this.extracting = false;
            }
        },
        factsSignature(item) {
            // 재추출 완료를 판별할 값 묶음 — 내용이 바뀌면 이 서명이 달라진다
            return [item.entries, item.facts_count, item.model,
                item.estimated_cost, item.extracted_at].join('|');
        },
        reconcileExtracting() {
            const byId = {};
            this.attachments.forEach(a => { byId[a.storage_id] = a; });
            this.extractingIds = this.extractingIds.filter(id => {
                const cur = byId[id];
                if (!cur) return false;                       // 목록에서 사라짐
                // 새 backend 응답이 있으면 client snapshot 보다 서버 상태를 신뢰한다.
                if (['pending', 'extracting', 'extracted'].includes(cur.status)) {
                    return cur.status === 'extracting';
                }
                const snap = this.extractSnapshots[id];
                // 신규 추출: 캐시 행이 생기면(extracted) 완료
                if (snap === undefined) return !cur.extracted;
                // 재추출: 값이 그대로면 아직 진행 중 (기존 행을 덮어쓰는 방식이라
                // extracted 여부로는 완료를 알 수 없다)
                return this.factsSignature(cur) === snap;
            });
            // 끝난 항목의 스냅샷 정리
            Object.keys(this.extractSnapshots).forEach(id => {
                if (!this.extractingIds.includes(Number(id))) {
                    this.$delete(this.extractSnapshots, id);
                }
            });
            if (!this.extractingIds.length) this.stopPolling();
        },
        hasActiveExtractions() {
            // client hint(extractingIds) 또는 서버가 진행중으로 표시한 첨부가 있으면 true.
            return (
                this.extractingIds.length > 0
                || this.attachments.some(
                    item => this.serverAttachmentStatus(item) === 'extracting')
            );
        },
        // ── 추출 진행 폴링 ────────────────────────────────────────────
        // 서버 status(refresh-safe) 또는 client hint 가 진행중인 동안 목록을 주기적으로
        // 다시 불러 '추출 중' 이 하나씩 빠지는 것으로 완료를 확인한다.
        startPolling() {
            if (this.pollTimer) return;   // 이미 폴링 중이면 중복 시작하지 않는다.
            this.pollCount = 0;
            this.pollFailures = 0;
            this.pollingStopped = false;
            this.pollTimer = setInterval(() => {
                this.pollCount += 1;
                if (!this.hasActiveExtractions()) {
                    this.stopPolling();
                    return;
                }
                // 대용량은 분 단위라 넉넉히 보되(10초 x 60 = 10분) 무한 폴링은 막는다.
                // 멈춘 뒤에는 '자동 갱신' 안내를 내리고 새로고침을 권한다.
                if (this.pollCount > 60) {
                    this.stopPolling();
                    this.pollingStopped = true;
                    return;
                }
                if (!this.loadingList) this.loadAttachments(true);   // 폴링은 조용히 갱신
            }, 10000);
        },
        stopPolling() {
            if (this.pollTimer) {
                clearInterval(this.pollTimer);
                this.pollTimer = null;
            }
        },
        async downloadOriginal(item) {
            if (this.downloadingId) return;
            this.downloadingId = item.storage_id;
            try {
                const {data} = await this.$axios.get(
                    `/document-facts/${item.storage_id}/download-url`,
                );
                if (!data || !data.url) {
                    throw new Error('no url');
                }
                // presigned URL 은 Content-Disposition(attachment)이 붙어 있어
                // 새 탭이 열렸다 닫히며 바로 저장된다.
                window.open(data.url, '_blank');
            } catch (e) {
                const msg = getErrorMessage(e, '원본을 받을 수 없습니다.');
                this.$EventBus.$emit('toast-failure', '다운로드 실패', msg);
            } finally {
                this.downloadingId = null;
            }
        },
        async openFacts(item) {
            this.factsTitle = item.filename;
            this.factsData = null;
            this.factsModal = true;
            this.factsLoading = true;
            try {
                const {data} = await this.$axios.get(`/document-facts/${item.storage_id}`);
                this.factsData = data;
            } catch (e) {
                const msg = getErrorMessage(e, '추출 내용을 불러오지 못했습니다.');
                this.$EventBus.$emit('toast-failure', '조회 오류', msg);
                this.factsModal = false;
            } finally {
                this.factsLoading = false;
            }
        },
    },
};
</script>
